import { useFormik } from 'formik';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useChangePasswordMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const initialValues: ChangePasswordInput = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const validate = (input: ChangePasswordInput) => {
  const errors = {} as { [P in keyof ChangePasswordInput]: string | undefined };
  if (!input.currentPassword) {
    errors.currentPassword = '入力してください';
  }

  if (!input.newPassword) {
    errors.newPassword = '入力してください';
  } else if (!input.newPassword.match(/^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9!-/:-@[-{-~]{12,64}$/)) {
    errors.newPassword = '12～64文字の半角英数字記号で入力してください';
  }

  if (!input.confirmPassword) {
    errors.confirmPassword = '入力してください';
  } else if (input.newPassword !== input.confirmPassword) {
    errors.confirmPassword = 'パスワードが一致しません';
  }

  return errors;
};

export type ChangePasswordProps = {
  onCompleted?: () => void;
};

export const useChangePassword = ({ onCompleted }: ChangePasswordProps) => {
  const { showInfo, showError } = useGlobalToastHandler();
  const [_res, changePasswordMutation] = useChangePasswordMutation();

  const submit = useOnceSubmit<{ values: ChangePasswordInput; resetForm: () => void }>(
    {
      onSubmit: async (args: { values: ChangePasswordInput; resetForm: () => void }) => {
        const res = await changePasswordMutation({
          input: {
            CurrentPassword: args.values.currentPassword,
            NewPassword: args.values.newPassword,
          },
        });
        if (!res.data) return;
        if (res.data?.ChangePassword?.Code === 0) {
          args.resetForm();
          showInfo('パスワードが変更されました');
          onCompleted?.();
        } else {
          showError(res.data?.ChangePassword?.Message);
        }
      },
    },
    [changePasswordMutation],
  );

  const formik = useFormik<ChangePasswordInput>({
    initialValues,
    onSubmit: (values, { resetForm }) =>
      submit({
        values,
        resetForm,
      }),
    validate,
    validateOnBlur: false,
    validateOnChange: true,
  });

  return {
    formik,
  };
};
